
import { Component, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";
import {
  ChannelsPageGetter,
  ChannelsPageMutation,
  InfoChannelData
} from "@/components/channelsPage/types";
import { Namespace } from "@/store/types";
import DateTimeSection from "@/components/DateTimeSection.vue";
import { mixins } from "vue-class-component";
import { EpgHelpers } from "@/mixins/EpgHelpers";
import { EpgItem } from "@/services/apiService/api.types";
import Swiper, { SwiperOptions } from "swiper";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { EventBus } from "@/main";
import { ChannelChangeGetter } from "@/components/channelChange/types";
import { DailyEpg } from "@/components/overlay/types";
import { getEpgDates } from "@/helpers/utils";
import dayjs from "dayjs";
import { RouteName } from "@/router/types";

@Component({
  components: { DateTimeSection }
})
export default class EpgModal extends mixins(EpgHelpers) {
  @Mutation(ChannelsPageMutation.setActiveChannelOverlayInfo, {
    namespace: Namespace.ChannelsPage
  })
  setActiveInfoScreenChannelInfo!: (channelInfo?: InfoChannelData) => void;

  @Mutation(ChannelsPageMutation.setShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowEpgScreen!: () => void;

  @Mutation(ChannelsPageMutation.setActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  setActiveInfoScreenEpgItem!: (epgItem?: EpgItem) => void;

  @Mutation(ChannelsPageMutation.setShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowInfoScreen!: (showInfoScreen: boolean) => void;

  @Getter(ChannelsPageGetter.getActiveChannelOverlayInfo, {
    namespace: Namespace.ChannelsPage
  })
  getActiveChannelOverlayInfo!: InfoChannelData | undefined;

  @Getter(ChannelsPageGetter.getActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  getActiveInfoScreenEpgItem!: EpgItem | undefined;

  @Getter(ChannelsPageGetter.getShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowEpgScreen!: boolean;

  @Getter(ChannelsPageGetter.getShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowInfoScreen!: boolean;

  @Getter(ChannelChangeGetter.GetOffset, {
    namespace: Namespace.ChannelsPage
  })
  getOffset!: number;

  epgList: EpgItem[] = [];

  dailyEpgLists: Array<EpgItem[]> = [];

  epgDates: DailyEpg[] = [];

  swiperOptions: SwiperOptions = {
    keyboard: {
      enabled: true
    },
    direction: "vertical",
    centeredSlides: true,
    slidesPerView: 5,
    spaceBetween: 30
  };

  swiperOptionsHorizontal: SwiperOptions = {
    keyboard: {
      enabled: true
    },
    centeredSlides: true,
    slidesPerView: 3,
    spaceBetween: 100
  };

  stopClickPropagation = false;
  showSwiper = false;

  dateSwiper: Swiper | undefined;
  horizontalSwiper: Swiper | undefined;
  epgSwipers: Swiper[] = [];

  async mounted(): Promise<void> {
    if (this.getShowEpgScreen) {
      if (this.getActiveChannelOverlayInfo) {
        //fetch epg lists for given dates
        this.epgDates = getEpgDates();
        const channelId = this.getActiveChannelOverlayInfo.channelId;
        //fetch epg data
        for (const epgDate of this.epgDates) {
          const index = this.epgDates.indexOf(epgDate);
          this.dailyEpgLists[index] = await this.fetchChannelEpg(
            channelId,
            epgDate.date
          );

          //trigger slider initialization only when data is fetched(sliders are rendered when all data is present)
          if (this.dailyEpgLists.length === 10) {
            this.showSwiper = true;

            this.initializeSwipers();
          }
        }
      }
      if (!this.getShowInfoScreen) {
        SpatialNavigation.pause();
        SpatialNavigation.focus("#epgModal");
      }
    }
  }

  get imgPlaceholder(): string {
    return require("/src/assets/images/square-image-placeholder.jpg");
  }

  get channelName(): string {
    return this.getActiveChannelOverlayInfo?.channelName
      ? this.getActiveChannelOverlayInfo.channelName
      : "";
  }

  get bgImage(): string | undefined {
    if (this.showSwiper) {
      return this.updateActiveEpgItem(this.dailyEpgLists[6])?.image;
    }

    return undefined;
  }

  //returns active epg swiper
  get swiperInstance(): Swiper | undefined {
    if (this.horizontalSwiper)
      return this.epgSwipers[this.horizontalSwiper?.activeIndex];
    return undefined;
  }

  initializeSwipers(): void {
    //create all required swiper instances
    this.$nextTick(() => {
      this.dateSwiper = new Swiper(
        ".swiper-header",
        this.swiperOptionsHorizontal
      );

      this.horizontalSwiper = new Swiper(
        "#swiperContent",
        this.swiperOptionsHorizontal
      );

      //initializing epg swipers
      for (let i = 0; i < this.epgDates.length; i++) {
        const epgSwiperInstance = new Swiper(
          "#vertical" + this.epgDates[i].date,
          this.swiperOptions
        );
        this.epgSwipers.push(epgSwiperInstance);
      }

      if (this.horizontalSwiper) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        this.horizontalSwiper?.on("slideChangeTransitionEnd", () => {
          if (this.swiperInstance) {
            //setting event listeners for when slide is clicked
            this.swiperInstance.on("click", () => {
              const activeItem =
                this.dailyEpgLists[
                  this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
                ][this.swiperInstance ? this.swiperInstance.clickedIndex : 0];
              this.setActiveInfoScreenEpgItem(activeItem);
              this.setShowInfoScreen(true);
            });
          }
        });
      }

      // focusing on proper epg item
      this.focusActiveEpgSlider();
      this.focusActiveEpgItem();
      this.swiperInstance?.on("click", () => {
        const activeItem =
          this.dailyEpgLists[
            this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
          ][this.swiperInstance ? this.swiperInstance.clickedIndex : 0];
        this.setActiveInfoScreenEpgItem(activeItem);
        this.setShowInfoScreen(true);
        this.setShowInfoScreen(true);
      });
    });
  }

  closeOverlay(): void {
    if (this.$route.name === RouteName.Buffet) {
      EventBus.$emit("epgOverlayClosed", this.getActiveInfoScreenEpgItem?.id);
      this.setActiveInfoScreenChannelInfo();
      this.setShowEpgScreen();
      return;
    }

    EventBus.$emit(
      "epgOverlayClosed",
      this.getActiveChannelOverlayInfo?.channelId
    );
    this.setActiveInfoScreenChannelInfo();
    this.setShowEpgScreen();
  }

  focusActiveEpgItem(): void {
    let activeItem = this.getActiveInfoScreenEpgItem;
    if (
      !activeItem ||
      activeItem.channel_id !== this.getActiveChannelOverlayInfo?.channelId
    ) {
      activeItem = this.updateActiveEpgItem(
        this.dailyEpgLists[
          this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
        ]
      );
    }

    if (activeItem) {
      this.swiperInstance?.slideTo(
        this.dailyEpgLists[
          this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
        ].findIndex((epgItem) => epgItem.id === activeItem?.id)
      );
      this.setActiveInfoScreenEpgItem(activeItem);
    }
  }

  focusActiveEpgSlider(): void {
    let activeItem = this.getActiveInfoScreenEpgItem
      ? this.getActiveInfoScreenEpgItem
      : this.updateActiveEpgItem(
          this.dailyEpgLists[
            this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
          ]
        );
    let activeDate = activeItem
      ? dayjs(
          activeItem?.epgStarttime
            ? activeItem.epgStarttime
            : activeItem.starttime
        )
      : dayjs(); //if no epgItem is present to get the date from default to today

    const activeHorizontalSwiperIndex = this.epgDates.findIndex((item) =>
      dayjs(item.date).isSame(activeDate, "day")
    );

    this.horizontalSwiper?.slideTo(activeHorizontalSwiperIndex);
    this.dateSwiper?.slideTo(activeHorizontalSwiperIndex);
  }

  handleSwiperAction(event: KeyboardEvent): void {
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_ENTER:
        if (!this.stopClickPropagation) {
          console.log(this.horizontalSwiper ? this.horizontalSwiper : 0);
          this.setActiveInfoScreenEpgItem(
            this.dailyEpgLists[
              this.horizontalSwiper ? this.horizontalSwiper.activeIndex : 0
            ][this.swiperInstance ? this.swiperInstance.activeIndex : 0]
          );
          this.setShowInfoScreen(true);
          this.stopClickPropagation = true;
        }
        break;
      case KeySet.KEY_DOWN:
        break;
      case KeySet.KEY_BACK:
        this.closeOverlay();
        break;
    }
  }

  @Watch("getShowInfoScreen")
  handleInfoChange(newVal: boolean): void {
    if (!newVal && this.getShowEpgScreen) {
      SpatialNavigation.focus("#epgSwiperContainer");
      setTimeout(() => {
        this.stopClickPropagation = false;
      }, 50);
    }
  }

  //destroying all swiper listeners and instances
  beforeDestroy(): void {
    for (let i = 0; i < this.epgSwipers.length; i++) {
      this.epgSwipers[i].off("click");
      this.epgSwipers[i].destroy(true, true);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    this.horizontalSwiper?.off("slideChangeTransitionEnd");
    this.horizontalSwiper?.destroy(true, true);
    this.dateSwiper?.destroy(true, true);
  }
}
